import { useRef, useState, RefObject, Dispatch, SetStateAction, useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useApolloClient } from '@apollo/client';
import { noop } from 'lodash';

import useErnie from 'shared/hooks/use-ernie';
import useUser from 'src/hooks/use-user';
import usePaths from 'src/hooks/use-paths';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'src/hooks/use-cart';
import { useHeader } from 'src/dispensary/core-menu/components/header/use-header';
import { useModals, ModalNames } from 'src/components/modals';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { setLoyaltyData } from 'src/components/loyalty/loyalty.helpers';
import { ERNIE_TYPES } from 'shared/constants';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useLoginNudge } from 'src/dispensary/core-menu/components/header/login-nudge';
import { useDutchiePay } from 'src/payments/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGetDutchiePosLoyaltyConfigQuery } from 'types/graphql';

type UseUserButtonReturn = {
  isUserLoggedIn: boolean;
  firstName: string;
  isPopoverOpen: boolean;
  popoverTriggerRef: RefObject<HTMLButtonElement>;
  setIsPopoverOpen: Dispatch<SetStateAction<boolean>>;
  loggedInRoutes: { href: string; label: string; hide?: boolean }[];
  loyaltyPoints: string;
  handleLogout: () => Promise<void>;
  handleLogin: () => Promise<void>;
  handlePopoverLinkClick: (label: string) => void;
  handlePopoverToggle: (label: string) => void;
};

export const useUserButton = (): UseUserButtonReturn => {
  const Cart = useCart();
  const User = useUser();
  const apolloClient = useApolloClient();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isLoading = useObserver(() => User.loading);
  const firstName = useObserver(() => User.firstName);
  const birthday = useObserver(() => User.birthday);
  const phone = useObserver(() => User.phone);
  const hasLoyaltyAccount = useObserver(() => User.hasLoyaltyAccount);
  const isUserLoggedIn = isLoggedIn && !isLoading;

  const popoverTriggerRef = useRef<HTMLButtonElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { trackEvent } = useHeader();

  const { openModal } = useModals();
  const showErnie = useErnie();
  const { dismissNudge } = useLoginNudge();

  const { dispensary } = useDispensary();
  const isKioskProEnabled = useKioskPro();
  const isShowLoyaltyFlagEnabled = (dispensary?.storeSettings?.showLoyaltyRewards ?? false) || isKioskProEnabled;
  const isAlpineIQEnabled = dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled;
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;
  const isSpringbigEnabled = dispensary?.consumerDispensaryIntegrations?.springbig?.enabled;

  const customerId = User?.id;
  const shouldHideLoyaltyLink =
    !isShowLoyaltyFlagEnabled || !(isAlpineIQEnabled || isDutchiePOSLoyaltyEnabled || isSpringbigEnabled);

  const { shouldShowBankAccountTab } = useDutchiePay();
  const flags = useFlags();
  const isShowReferralsEnabled = flags['pos.customers.refer-a-friend.rollout'] ?? false;

  const pin = User.getLoyaltyPin(dispensary.id);

  const skip =
    !isShowLoyaltyFlagEnabled ||
    !isUserLoggedIn ||
    !phone ||
    (isAlpineIQEnabled && !pin) ||
    (isDutchiePOSLoyaltyEnabled && !customerId) ||
    (isSpringbigEnabled && !customerId);

  const { data } = useGetRewards({
    dispensaryId: dispensary.id,
    birthday,
    pin,
    phone,
    skip,
    customerId,
  });

  const rewardsData = data?.getRewardsV2;

  const { data: configQueryData } = useGetDutchiePosLoyaltyConfigQuery({
    variables: { dispensaryId: dispensary?.id ?? '' },
    skip: !isDutchiePOSLoyaltyEnabled,
  });
  const referralConfig = configQueryData?.getDutchiePosLoyaltyConfig?.referralConfig;

  const shouldHideReferralsLink = !(
    isShowLoyaltyFlagEnabled &&
    isDutchiePOSLoyaltyEnabled &&
    isShowReferralsEnabled &&
    !!referralConfig?.isEnabled &&
    !rewardsData?.isRevokedFromReferralProgram
  );

  useEffect(() => {
    if (
      data &&
      (rewardsData?.auth?.pinConfirmed ||
        (isDutchiePOSLoyaltyEnabled && customerId) ||
        (isSpringbigEnabled && rewardsData?.userHasWallet))
    ) {
      setLoyaltyData({
        user: User,
        cart: Cart,
        data,
        dispensaryId: dispensary.id,
      });
    }
  }, [data, rewardsData, isDutchiePOSLoyaltyEnabled, customerId, isSpringbigEnabled, User, Cart, dispensary.id]);

  const { href: baseHref } = usePaths();
  const loggedInRoutes = [
    {
      href: `${baseHref}/user/account`,
      label: 'My account',
    },
    {
      href: `${baseHref}/user/orders`,
      label: 'Orders',
    },
    {
      href: `${baseHref}/user/referrals`,
      label: 'Referrals',
      hide: shouldHideReferralsLink,
    },
    {
      href: `${baseHref}/user/loyalty`,
      label: 'Loyalty',
      hide: shouldHideLoyaltyLink,
    },
    {
      href: `${baseHref}/user/bank-account`,
      label: 'Payments',
      hide: !shouldShowBankAccountTab,
    },
    {
      href: `${baseHref}/user/preferences`,
      label: 'Preferences',
    },
  ];

  const handleLogout = async (): Promise<void> => {
    setIsPopoverOpen(false);
    User.logout();

    /**
     * Clear Apollo cache on logout. Use clearStore() instead of resetStore() to avoid
     * refetching ALL active queries at once, which can hang the browser.
     *
     * Queries will refetch lazily as components need data. Exclusive content updates
     * correctly because userSegmentIds in query variables changes on logout.
     */
    await apolloClient.clearStore().catch(noop);
    showErnie("You've been successfully logged out.", ERNIE_TYPES.SUCCESS);
    trackEvent('Logout');
  };

  const handleLogin = async (): Promise<void> => {
    dismissNudge();
    await openModal(ModalNames.login);

    /**
     * Clear Apollo cache on login. Use clearStore() instead of resetStore() to avoid
     * refetching ALL active queries at once, which can hang the browser.
     *
     * Queries will refetch lazily as components need data. User-specific content
     * updates correctly because userSegmentIds in query variables changes on login.
     *
     * Skip clearing store if user data query is currently in flight to avoid race
     * conditions during SSO login flow in embedded menus. This prevents Apollo Client
     * Invariant Violation: 16 errors when clearStore() is called while queryMe() is
     * executing.
     */
    if (!User.loading) {
      await apolloClient.clearStore().catch(noop);
    }
    setIsPopoverOpen(false);
    trackEvent('Login');
  };

  const handlePopoverLinkClick = (label: string): void => {
    setIsPopoverOpen(false);
    trackEvent(label);
  };

  const handlePopoverToggle = (label: string): void => {
    setIsPopoverOpen(!isPopoverOpen);
    trackEvent(label);
  };

  return {
    isUserLoggedIn,
    firstName,
    isPopoverOpen,
    popoverTriggerRef,
    setIsPopoverOpen,
    loggedInRoutes,
    loyaltyPoints: hasLoyaltyAccount ? rewardsData?.balance ?? '' : '',
    handleLogout,
    handleLogin,
    handlePopoverLinkClick,
    handlePopoverToggle,
  };
};
